/**
 * 採点APIエラーコード定義
 *
 * Lambda側とフロントエンド側で共有するエラーコード・メッセージの定義。
 * このファイルがSingle Source of Truth（単一の真実の情報源）です。
 *
 * 【編集時の注意】
 * - このファイルを編集した場合、Lambdaの再デプロイが必要です
 * - フロントエンド側は自動的にこのファイルを参照します
 */

/**
 * 採点APIエラー辞書
 *
 * 各エラーの構造:
 * - code: エラーコード（APIレスポンスに含まれる）
 * - lambdaMessage: Lambda側のエラーメッセージ（ログ・デバッグ用）
 * - userMessage: ユーザー向けメッセージ（nullの場合はデフォルトメッセージを表示）
 *
 * @type {Record<string, {code: string, lambdaMessage: string, userMessage: string | null}>}
 */
export const GRADING_ERRORS = {
  /** 端末の時刻がサーバー時刻と±5分以上ズレている */
  TIMESTAMP_INVALID: {
    code: 'TIMESTAMP_INVALID',
    lambdaMessage:
      '採点エラーが発生しました：ご利用端末の時刻設定がズレていると採点エラーが発生します。ご利用端末の時刻設定を見直していただき、再度テストを実施ください',
    userMessage:
      '採点エラーが発生しました：ご利用端末の時刻設定がズレていると採点エラーが発生します。ご利用端末の時刻設定を見直していただき、再度テストを実施ください',
  },
  /** リクエストボディが不正なJSON */
  JSON_PARSE_ERROR: {
    code: 'JSON_PARSE_ERROR',
    lambdaMessage: 'リクエストボディが有効なJSON形式ではありません。',
    userMessage: null,
  },
  /** 解答数が期待値と不一致 */
  QUESTION_COUNT_MISMATCH: {
    code: 'QUESTION_COUNT_MISMATCH',
    lambdaMessage: '解答数が期待値と一致しません。',
    userMessage: null,
  },
  /** 存在しない問題IDを送信 */
  QUESTION_NOT_FOUND: {
    code: 'QUESTION_NOT_FOUND',
    lambdaMessage: '問題IDが見つかりません。有効な問題IDを指定してください。',
    userMessage: null,
  },
  /** コースID未指定 */
  COURSE_ID_MISSING: {
    code: 'COURSE_ID_MISSING',
    lambdaMessage: 'コースIDが指定されていません。',
    userMessage: null,
  },
  /** 試験ID未指定 */
  EXAMINATION_ID_MISSING: {
    code: 'EXAMINATION_ID_MISSING',
    lambdaMessage: '試験IDが指定されていません。',
    userMessage: null,
  },
  /** 解答未指定 */
  QUESTIONS_MISSING: {
    code: 'QUESTIONS_MISSING',
    lambdaMessage: '問題の解答が指定されていないか、無効な形式です。',
    userMessage: null,
  },
  /** S3バケット名未設定 */
  BUCKET_NAME_MISSING: {
    code: 'BUCKET_NAME_MISSING',
    lambdaMessage:
      'S3バケット名が指定されていません。環境変数 "S3_BUCKET_NAME" を設定してください。',
    userMessage: null,
  },
  /** S3オブジェクトが見つからない */
  S3_OBJECT_NOT_FOUND: {
    code: 'S3_OBJECT_NOT_FOUND',
    lambdaMessage: '指定されたファイルが見つかりません。',
    userMessage: null,
  },
  /** S3バケットが見つからない */
  S3_BUCKET_NOT_FOUND: {
    code: 'S3_BUCKET_NOT_FOUND',
    lambdaMessage: '指定されたバケットが見つかりません。',
    userMessage: null,
  },
  /** S3アクセス拒否 */
  S3_ACCESS_DENIED: {
    code: 'S3_ACCESS_DENIED',
    lambdaMessage:
      'S3オブジェクトへのアクセスが拒否されました。権限を確認してください。',
    userMessage: null,
  },
  /** S3オブジェクトが不正なJSON */
  S3_JSON_INVALID: {
    code: 'S3_JSON_INVALID',
    lambdaMessage: 'S3オブジェクトが有効なJSON形式ではありません。',
    userMessage: null,
  },
  /** 試験データが見つからない */
  EXAMINATION_NOT_FOUND: {
    code: 'EXAMINATION_NOT_FOUND',
    lambdaMessage: '指定された試験が見つかりません。',
    userMessage: null,
  },
  /** HTTPメソッド不正 */
  METHOD_NOT_ALLOWED: {
    code: 'METHOD_NOT_ALLOWED',
    lambdaMessage: 'この API は POST メソッドのみサポートしています。',
    userMessage: null,
  },
  /** 内部エラー */
  INTERNAL_ERROR: {
    code: 'INTERNAL_ERROR',
    lambdaMessage: '予期しないエラーが発生しました。',
    userMessage: null,
  },
};

/**
 * エラーコードの一覧（内部使用）
 * @type {Record<string, string>}
 */
const ERROR_CODES = Object.fromEntries(
  Object.entries(GRADING_ERRORS).map(([key, value]) => [key, value.code])
);

/**
 * エラーコードに対応するユーザー向けメッセージを取得する（フロントエンド用）
 *
 * @param {string | undefined} errorCode - 採点APIから返却されたエラーコード
 * @returns {string} ユーザー向けメッセージ
 */
export const getGradingErrorMessage = errorCode => {
  if (!errorCode) {
    return '採点の実行に失敗しました';
  }

  const errorDef = GRADING_ERRORS[errorCode];

  // 辞書に定義があり、ユーザー向けメッセージが設定されている場合はそれを返す
  if (errorDef?.userMessage) {
    return errorDef.userMessage;
  }

  // それ以外はデフォルトメッセージ（エラーコード付き）を返す
  return `採点の実行に失敗しました: ${errorCode}`;
};

/**
 * エラーコードに対応するLambdaメッセージを取得する（内部使用）
 *
 * @param {string} errorCode - エラーコード
 * @param {string | null} [dynamicPart] - 動的に追加する情報（ファイル名、ID等）
 * @returns {string} エラーメッセージ
 */
const getLambdaErrorMessage = (errorCode, dynamicPart = null) => {
  const errorDef = GRADING_ERRORS[errorCode];
  if (!errorDef) {
    return '予期しないエラーが発生しました。';
  }
  if (dynamicPart) {
    return `${errorDef.lambdaMessage}: ${dynamicPart}`;
  }
  return errorDef.lambdaMessage;
};

/**
 * Lambdaエラーレスポンス用のエラーオブジェクトを生成する
 *
 * @param {string} errorKey - GRADING_ERRORSのキー（例: 'TIMESTAMP_INVALID'）
 * @param {string | null} [dynamicPart] - 動的に追加する情報（ファイル名、ID等）
 * @returns {{errorCode: string, message: string}} エラーオブジェクト
 */
export const createLambdaError = (errorKey, dynamicPart = null) => ({
  errorCode: ERROR_CODES[errorKey],
  message: getLambdaErrorMessage(errorKey, dynamicPart),
});
